/**
 * The Fitvent mark, and the `fitvent` / `myfitvent` lockups.
 *
 * Drawn inline rather than loaded from `/brand/*.svg` for one reason: the mark
 * has a light-ground version (teal on ink) and a dark-ground version (bright
 * cyan on bone), and swapping an `<img src>` on theme change means either a
 * flash of the wrong mark or a JS round-trip after mount. Inline paths driven by
 * CSS variables switch with the theme in the same paint as everything else.
 *
 * The geometry is copied exactly from `brand/fitvent-mark-{light,dark}.svg`.
 * The two files differ ONLY in their two fills, so this is one shape with two
 * colour pairs, not a redraw. The source files are still shipped in
 * `public/brand/` for favicons, emails, PDFs and anywhere an SVG file is what is
 * wanted.
 *
 * The colours below are brand constants, NOT theme tokens. They must keep
 * resolving to the pairs in the brand pack:
 *
 *     light ground   #006D6D accent, #10201D ink
 *     dark ground    #2ADFDF accent, #F4FAF8 bone
 *
 * "Never recolour" from the brand README applies here. Do not point these at
 * --fvd-teal or --fvd-ink: those are UI tokens and are free to move, which would
 * silently drag the logo with them.
 */

export function FitventMark({ size = 17 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label="Fitvent"
      style={{ flex: 'none', display: 'block' }}
    >
      <polygon points="8,14 92,14 92,28 22,28" fill="var(--fvd-mark-accent)" />
      <polygon points="34,44 92,44 92,58 48,58" fill="var(--fvd-mark-ink)" />
      <polygon points="68,74 92,74 92,88 78,88" fill="var(--fvd-mark-ink)" />
    </svg>
  );
}

/**
 * Mark plus wordmark, the organiser-side lockup.
 *
 * The wordmark is live text rather than the packaged SVG lockup, which the
 * handoff asks for explicitly: those lockups reference Schibsted Grotesk by
 * name and fall back to a default face wherever the font has not loaded. Live
 * text also stays crisp at 15px. If we ever ship the SVGs instead, the brand
 * owner has to supply outlined-path versions first.
 */
export function FitventLockup({ markSize = 17 }: { markSize?: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <FitventMark size={markSize} />
      <span className="fvd-wordmark">fitvent</span>
    </span>
  );
}

/** The athlete-side lockup: `my` takes the accent, `fitvent` takes the ink. */
export function MyFitventLockup({ markSize = 17 }: { markSize?: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <FitventMark size={markSize} />
      <span className="fvd-wordmark">
        <span className="fvd-wordmark-my">my</span>fitvent
      </span>
    </span>
  );
}
